/**
 * MacroDilReconciliationCard.jsx — iter937 (Feb 2026)
 *
 * Clinical rewrite based on endocrinologist + UX/PM review (Feb 2026):
 *   • Two clearly-separated cards side-by-side: "Nutrition Plan" (kcal/macros)
 *     vs "Daily Insulin Budget" (DIL ceiling + reference values).
 *   • Real DIL formula shown: DIL = 0.69 × Protein + max(0, Net Carbs).
 *   • 3-basket comparison uses the ACTUAL v2 formula (fiber-aware net carbs),
 *     not a made-up multiplier. Numbers come out around 59 / 95 / 97 DIL from
 *     carbs for lentils / white rice / juice respectively.
 *   • Softer, science-defensible wording ("can shift by 50-70%" not "triple").
 *   • Patient-friendly terminology: no "reference", no "ceiling" in the pill
 *     — use "Дневен лимит" and "Помага да избирате храни".
 *   • 14-day mean vs limit with delta badge (props.dilAvg).
 *   • BG + EN translations.
 *
 * Props:
 *   • breakdown — dil_breakdown envelope from /api/health-intelligence/summary
 *   • macros    — { calories, protein, carbs, fat } from report.standard_metrics
 *   • dilAvg    — number | null (14-day mean DIL)
 *   • onRegenerate — async () => void (re-generates the AI health report)
 *
 * Educational only. Not a medical device.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, BookOpen, Info, Utensils, Droplet,
  ArrowRight, Save,
} from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/App";

// ─── Locale strings ──────────────────────────────────────────────────────
const L = {
  bg: {
    section_title: "Хранителен план vs Инсулинов бюджет",
    section_sub: "Двете карти НЕ се конкурират — първата казва колко да ядеш, втората как да избираш.",
    regen: "Прегенерирай",
    regen_title: "Прегенерирай AI-доклада според актуалните ти данни",

    // Card 1 — Nutrition
    n_title: "🍽️ Вашият хранителен план",
    n_sub: "Колко да изядете днес",
    n_when_label: "Кога да гледате тази карта?",
    n_when_body: "Когато планирате менюто или проверявате калориите за деня.",
    n_kcal: "Калории",
    n_protein: "Белтъчини",
    n_carbs: "Въглехидрати",
    n_fat: "Мазнини",
    n_used_for: "Използва се за:",
    n_uses: [
      "планиране на менюто",
      "проследяване на калориите",
      "постигане на целта (загуба/поддръжка/маса)",
    ],

    // Divider between the two cards
    divider_vs: "vs",
    divider_hint: "не се конкурират — допълват се",

    // Card 2 — DIL
    d_title: "🩸 Вашият дневен инсулинов бюджет",
    d_sub: "Помага да избирате храни с по-ниско инсулиново натоварване",
    d_when_label: "Кога да гледате тази карта?",
    d_when_body: "Когато избирате между две храни или продукти в магазина.",
    d_limit_label: "Дневен лимит",
    d_limit_unit: "DIL",
    // iter1100 — CTA когато липсват задължителни профилни полета.
    d_gap_title: "Попълнете профила за личен DIL",
    d_gap_lead: "Без ръст, тегло и възраст не можем да изчислим инсулинов бюджет.",
    d_gap_cta: "Попълни профила →",
    d_gap_inline_save: "Запази и изчисли",
    d_gap_saving: "Запазване…",
    d_gap_saved: "Записано ✓",
    d_gap_save_err: "Грешка при запис",
    d_gap_fields: { weight: "тегло", height: "ръст", age: "възраст" },
    d_gap_placeholders: { weight: "kg", height: "cm", age: "г." },
    d_gap_labels: { weight: "Тегло", height: "Ръст", age: "Възраст" },
    // iter1100 — Body fat estimation warning (partial data).
    d_bf_missing_title: "Оценка без телесен състав",
    d_bf_missing_hint: "Добавете обиколка на талия, ханш и шия за по-точна корекция.",
    d_bf_missing_cta: "Добави измервания",
    d_bf_labels: { waist: "Талия (cm)", hip: "Ханш (cm)", neck: "Шия (cm)" },
    d_bf_save: "Запази обиколките",
    d_bf_saving: "Запазване…",
    d_formula_label: "Формула",
    d_formula: "Нетни въглехидрати + 0.69 × Белтък",
    d_reference_title: "Референтни стойности за изчисление",
    d_ref_net_carbs: "Референтни нетни въглехидрати",
    d_ref_net_carbs_hint: "общи − фибри",
    d_ref_protein: "Референтен белтък",
    d_protein_contribution: "Инсулинов принос от белтъка",
    d_used_for: "Използва се за:",
    d_uses: [
      "избор между две храни",
      "сравняване на продукти",
      "по-нисък инсулинов отговор",
    ],
    // iter938 — Hormonal adjustment hint
    d_hormonal_intro: "с приложена хормонална корекция",
    d_hormonal_base: "База (без корекция)",
    d_hormonal_factor: "Естрогенен коефициент",
    d_hormonal_estrogen: "Мастна маса-медииран естрогенен статус",
    d_hormonal_why: "Мастната тъкан ароматизира тестостерон в естроген. По-висок естрогенен товар → по-стриктен инсулинов таван.",

    // Basket comparison
    b_title: "Сравнение: 97g въглехидрати от различни източници",
    b_intro_a: "При еднакво количество въглехидрати изборът на храна може да ",
    b_intro_bold: "промени инсулиновия отговор с 50-70%",
    b_intro_b: " заради разликата в съдържанието на фибри.",
    b_carbs_from: "въглехидрати от",
    b_net_carbs: "нетни",
    b_within_budget: "в бюджета",
    b_over_budget: "над бюджета",
    b_over_by: "над с",
    b_note: "Забележка: инсулиновият отговор в реалния живот зависи и от степента на обработка, комбинирането на храните, мазнините и белтъка в блюдото. DIL е инструмент за оценка на качеството, а не абсолютна прогноза.",

    // 14-day
    live_title: "14-дневна ти средна",
    live_under: "под лимита",
    live_over: "над лимита",

    // Flow
    flow_1: "Колко",
    flow_2: "Какво",
    flow_3: "Метаболитен отговор",

    // Science drawer
    sci_toggle_show: "Покажи научната основа",
    sci_toggle_hide: "Скрий научната основа",
    sci_why_two: "Защо два показателя?",
    sci_why_two_body: "Nutrition target ти казва колко енергия и градивни блокове са нужни (kcal, белтък, мазнини, въглехидрати). DIL измерва специфичното инсулиново усилие на храните — един и същ калорийно-балансиран ден може да е инсулинемично щадящ или натоварен според избора на въглехидратния източник.",
    sci_coeff: "Коефициентът 0.69 — Nutramilo v2.0 регресионен коефициент за инсулиновия принос на белтъка при смесен-макронутриентен режим.",
    sci_your_protocol: "Твоят протокол",
    sci_refs: "Референции",
    sci_edu_only: "Образователно съдържание. Не заменя лекарска консултация.",
  },
  en: {
    section_title: "Nutrition Plan vs Daily Insulin Budget",
    section_sub: "The two cards do NOT compete — the first says how much to eat, the second how to choose.",
    regen: "Regenerate",
    regen_title: "Regenerate the AI report based on your latest data",

    n_title: "🍽️ Your Nutrition Plan",
    n_sub: "How much to eat today",
    n_when_label: "When to look at this card?",
    n_when_body: "When planning your menu or checking today's calories.",
    n_kcal: "Calories",
    n_protein: "Protein",
    n_carbs: "Carbs",
    n_fat: "Fat",
    n_used_for: "Used for:",
    n_uses: [
      "menu planning",
      "calorie tracking",
      "meeting your goal (loss/maintenance/gain)",
    ],

    divider_vs: "vs",
    divider_hint: "not competing — they complement each other",

    d_title: "🩸 Your Daily Insulin Budget",
    d_sub: "Helps you pick foods with lower insulin load",
    d_when_label: "When to look at this card?",
    d_when_body: "When choosing between two foods or products at the store.",
    d_limit_label: "Daily limit",
    d_limit_unit: "DIL",
    // iter1100
    d_gap_title: "Complete your profile for a personal DIL",
    d_gap_lead: "We can't compute an accurate insulin budget without height, weight and age.",
    d_gap_cta: "Complete profile →",
    d_gap_inline_save: "Save and compute",
    d_gap_saving: "Saving…",
    d_gap_saved: "Saved ✓",
    d_gap_save_err: "Save failed",
    d_gap_fields: { weight: "weight", height: "height", age: "age" },
    d_gap_placeholders: { weight: "kg", height: "cm", age: "yrs" },
    d_gap_labels: { weight: "Weight", height: "Height", age: "Age" },
    d_bf_missing_title: "Estimate without body composition",
    d_bf_missing_hint: "Add waist, hip and neck measurements for a more accurate correction.",
    d_bf_missing_cta: "Add measurements",
    d_bf_labels: { waist: "Waist (cm)", hip: "Hip (cm)", neck: "Neck (cm)" },
    d_bf_save: "Save measurements",
    d_bf_saving: "Saving…",
    d_formula_label: "Formula",
    d_formula: "Net carbs + 0.69 × Protein",
    d_reference_title: "Reference values used in the calculation",
    d_ref_net_carbs: "Reference net carbs",
    d_ref_net_carbs_hint: "total − fiber",
    d_ref_protein: "Reference protein",
    d_protein_contribution: "Protein insulin contribution",
    d_used_for: "Used for:",
    d_uses: [
      "choosing between two foods",
      "comparing products",
      "targeting a lower insulin response",
    ],
    // iter938 — Hormonal adjustment hint
    d_hormonal_intro: "with hormonal correction applied",
    d_hormonal_base: "Base (unadjusted)",
    d_hormonal_factor: "Estrogen coefficient",
    d_hormonal_estrogen: "Fat-mass-mediated estrogen status",
    d_hormonal_why: "Adipose tissue aromatizes testosterone into estrogen. Higher estrogen load → stricter insulin ceiling.",

    b_title: "Comparison: 97g of carbs from different sources",
    b_intro_a: "At the same carb amount, food choice can ",
    b_intro_bold: "shift insulin response by 50-70%",
    b_intro_b: " because of the difference in fiber content.",
    b_carbs_from: "carbs from",
    b_net_carbs: "net",
    b_within_budget: "within budget",
    b_over_budget: "over budget",
    b_over_by: "over by",
    b_note: "Note: real-life insulin response also depends on processing, food combinations, fat and protein in the meal. DIL is a food-quality tool, not an absolute prediction.",

    live_title: "Your 14-day mean",
    live_under: "under the limit",
    live_over: "over the limit",

    flow_1: "How much",
    flow_2: "What to choose",
    flow_3: "Metabolic response",

    sci_toggle_show: "Show the science",
    sci_toggle_hide: "Hide the science",
    sci_why_two: "Why two indicators?",
    sci_why_two_body: "The nutrition target tells you how much energy and building blocks you need (kcal, protein, fat, carbs). DIL measures the specific insulin effort of foods — the same calorie-balanced day can be insulin-sparing or insulin-heavy depending on the carb source.",
    sci_coeff: "The 0.69 coefficient — Nutramilo v2.0 regression coefficient for the insulin contribution of protein in a mixed-macronutrient regimen.",
    sci_your_protocol: "Your protocol",
    sci_refs: "References",
    sci_edu_only: "Educational content. Does not replace medical consultation.",
  },
};

// ─── Basket definitions (fiber content per 100g CHO source) ──────────────
// Numbers reflect realistic fiber ratios in the Bulgarian Food DB:
//   • Легуми/овес/пълнозърнести — ~40% fiber-to-carb ratio
//   • Рафинирани зърнени — ~2% fiber-to-carb ratio
//   • Свободни захари/сокове — 0% fiber
const BASKETS_BG = [
  { id: "low",  emoji: "🌿", label: "Леща, боб, овес, пълнозърнести", fiber_ratio: 0.40, color: "emerald", hint: "Богати на фибри · нисък GI" },
  { id: "mid",  emoji: "🍚", label: "Бял ориз, паста, бял хляб",       fiber_ratio: 0.02, color: "amber",   hint: "Рафинирани · среден-висок GI" },
  { id: "high", emoji: "🍬", label: "Сокове, сладкиши, десерти",       fiber_ratio: 0.00, color: "rose",    hint: "Свободни захари · висок GI" },
];
const BASKETS_EN = [
  { id: "low",  emoji: "🌿", label: "Lentils, beans, oats, whole grains", fiber_ratio: 0.40, color: "emerald", hint: "Fiber-rich · low GI" },
  { id: "mid",  emoji: "🍚", label: "White rice, pasta, white bread",     fiber_ratio: 0.02, color: "amber",   hint: "Refined · medium-high GI" },
  { id: "high", emoji: "🍬", label: "Juices, sweets, desserts",           fiber_ratio: 0.00, color: "rose",    hint: "Free sugars · high GI" },
];

// ─── Component ────────────────────────────────────────────────────────────
export default function MacroDilReconciliationCard({ breakdown, gap, macros, dilAvg, onRegenerate, onRefresh }) {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { API } = useAuth();
  const lang = language === "en" ? "en" : "bg";
  const t = L[lang];
  const baskets = lang === "en" ? BASKETS_EN : BASKETS_BG;

  // iter1101 — Inline gap field values + save state.
  const [gapValues, setGapValues] = useState({});
  const [gapSaving, setGapSaving] = useState(false);
  const gapMissing = Array.isArray(gap?.missing_fields) ? gap.missing_fields : [];
  const gapReady = gapMissing.length > 0
    && gapMissing.every((f) => {
        const v = gapValues[f];
        return v !== undefined && v !== null && String(v).trim() !== "" && !isNaN(Number(v));
      });

  const handleGapSave = async () => {
    if (!gapReady || gapSaving) return;
    setGapSaving(true);
    try {
      const payload = {};
      gapMissing.forEach((f) => {
        const n = Number(gapValues[f]);
        if (!isNaN(n) && n > 0) payload[f] = n;
      });
      // 1) Save the missing fields via progressive endpoint (dot-path, ~200ms).
      await axios.patch(`${API}/user/profile/progressive`, payload);
      toast.success(t.d_gap_saved);
      // 2) Refresh summary immediately → gap disappears, DIL number appears.
      if (typeof onRefresh === "function") await onRefresh();
      setGapSaving(false);
      // 3) Fire-and-forget report regen (LLM call ~30s). We don't block the UI
      //    on this — DIL is already usable. When regen completes, poll refresh
      //    picks up BMR/TDEE/kcal. Toast shown after done.
      (async () => {
        try {
          await axios.post(`${API}/user/health-report/regenerate`, { language: lang });
          if (typeof onRefresh === "function") await onRefresh();
          toast.success(lang === "bg" ? "BMR/TDEE обновени ✓" : "BMR/TDEE updated ✓");
        } catch { /* best-effort — DIL вече работи. */ }
      })();
    } catch (e) {
      toast.error(t.d_gap_save_err);
      setGapSaving(false);
    }
  };

  // iter1101 — Body-fat inline save (neck/waist/hip). Determines required
  // fields по пол (жени → всичките 3, мъже → neck+waist).
  const [bfValues, setBfValues] = useState({});
  const [bfSaving, setBfSaving] = useState(false);
  const bfSex = String(breakdown?.sex_used || "female").toLowerCase();
  const bfRequired = bfSex === "female" ? ["waist", "hip", "neck"] : ["waist", "neck"];
  const bfReady = bfRequired.every((f) => {
    const v = bfValues[f];
    return v !== undefined && v !== null && String(v).trim() !== "" && Number(v) > 0;
  });

  const handleBfSave = async () => {
    if (!bfReady || bfSaving) return;
    setBfSaving(true);
    try {
      const payload = {};
      bfRequired.forEach((f) => {
        const n = Number(bfValues[f]);
        if (!isNaN(n) && n > 0) payload[f] = n;
      });
      await axios.patch(`${API}/user/profile/progressive`, payload);
      toast.success(t.d_gap_saved);
      if (typeof onRefresh === "function") await onRefresh();
      setBfSaving(false);
      // Background regen so body-fat % + BMR корекциите се обновят.
      (async () => {
        try {
          await axios.post(`${API}/user/health-report/regenerate`, { language: lang });
          if (typeof onRefresh === "function") await onRefresh();
          toast.success(lang === "bg" ? "Телесен състав обновен ✓" : "Body comp updated ✓");
        } catch { /* best-effort */ }
      })();
    } catch (e) {
      toast.error(t.d_gap_save_err);
      setBfSaving(false);
    }
  };

  const [showScience, setShowScience] = useState(false);
  const [regen, setRegen] = useState(false);

  // Numbers
  const kcal     = macros?.calories != null ? Math.round(macros.calories) : null;
  const proteinG = macros?.protein  != null ? Math.round(macros.protein)  : null;
  const carbsG   = macros?.carbs    != null ? Math.round(macros.carbs)    : null;
  const fatG     = macros?.fat      != null ? Math.round(macros.fat)      : null;

  const ceilingG = breakdown?.ceiling_g   != null ? Math.round(breakdown.ceiling_g) : null;
  const refNetCarbsG = breakdown?.carb_target_g    != null ? Math.round(breakdown.carb_target_g * 10) / 10 : null;
  const refProteinG  = breakdown?.protein_target_g != null ? Math.round(breakdown.protein_target_g * 10) / 10 : null;
  const proteinContribution = refProteinG != null ? Math.round(refProteinG * 0.69 * 10) / 10 : null;
  const goalLabel = lang === "en" ? breakdown?.goal_label_en : breakdown?.goal_label_bg;
  const pmids = breakdown?.pmid || [];

  // iter938 — Hormonal adjustment context (only meaningful for females).
  // For males the backend forces hormonal_factor = 1.0 → we render a
  // single clean number without the modifier hint.
  const hormonalFactor  = breakdown?.hormonal_adjustment_factor ?? 1.0;
  const baseCeiling     = breakdown?.base_ceiling_g != null ? Math.round(breakdown.base_ceiling_g) : null;
  const estrogenLevel   = breakdown?.estrogen_risk_level || null;
  const estrogenLabel   = lang === "bg"
    ? breakdown?.estrogen_risk_label_bg
    : (estrogenLevel === "high" ? "🔴 High" : estrogenLevel === "moderate" ? "🟠 Moderate" : "🟢 Low");
  const sexUsed = breakdown?.sex_used || "female";
  const showHormonalHint = sexUsed === "female" && Math.abs(hormonalFactor - 1.0) > 0.005 && baseCeiling != null;

  // ── Basket math using the REAL v2 formula (fiber-aware) ────────────────
  // We compare only the CARB CONTRIBUTION to DIL (net_carbs), holding
  // protein constant, so the patient sees the isolated effect of food
  // quality on the same daily carb amount.
  const basketDil = (basket) => {
    if (carbsG == null) return null;
    const fiber = carbsG * basket.fiber_ratio;
    const netCarbs = Math.max(0, carbsG - fiber);
    return Math.round(netCarbs);
  };

  const handleRegen = async () => {
    if (!onRegenerate || regen) return;
    setRegen(true);
    try { await onRegenerate(); } finally { setRegen(false); }
  };

  // ── Palette helpers ────────────────────────────────────────────────────
  const cardShell = isDark
    ? "bg-slate-900/40 border-slate-700/60"
    : "bg-white border-slate-200";
  const nutritionAccent = isDark ? "text-emerald-300" : "text-emerald-700";
  const dilAccent       = isDark ? "text-cyan-300"    : "text-cyan-700";

  return (
    <section
      className={`rounded-2xl border p-4 mt-3 ${
        isDark ? "bg-gradient-to-br from-indigo-950/25 via-slate-900/60 to-cyan-900/15 border-indigo-500/25"
               : "bg-gradient-to-br from-indigo-50 via-white to-cyan-50 border-indigo-200"
      }`}
      data-testid="macro-dil-reconciliation-card"
    >
      {/* Header + regenerate */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-extrabold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>🍽️ Хранителен план</span>
            <span className={`mx-1.5 text-[10px] font-black align-middle px-1.5 py-0.5 rounded ${
              isDark ? "bg-slate-700/60 text-slate-300" : "bg-slate-200 text-slate-600"
            }`}>{t.divider_vs}</span>
            <span className={isDark ? "text-cyan-300" : "text-cyan-700"}>🩸 Инсулинов бюджет</span>
          </div>
          <p className={`text-[11px] mt-1 leading-snug font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
              isDark ? "bg-slate-800/60 text-slate-200" : "bg-slate-100 text-slate-700"
            }`}>ℹ️ {t.divider_hint}</span>{" "}
            {t.section_sub}
          </p>
        </div>
        {onRegenerate && (
          <button
            type="button"
            onClick={handleRegen}
            disabled={regen}
            className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
              isDark ? "bg-indigo-500/20 text-indigo-100 hover:bg-indigo-500/30"
                     : "bg-indigo-600 text-white hover:bg-indigo-700"
            } disabled:opacity-60`}
            data-testid="mdr-regenerate-btn"
            title={t.regen_title}
          >
            {regen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {t.regen}
          </button>
        )}
      </div>

      {/* Visual flow */}
      <div className="flex items-center justify-center gap-2 mb-3 text-[10.5px] font-semibold">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
          isDark ? "bg-emerald-500/15 text-emerald-200" : "bg-emerald-100 text-emerald-800"
        }`}>🍽️ {t.flow_1}</span>
        <ArrowRight className={`w-3 h-3 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
          isDark ? "bg-cyan-500/15 text-cyan-200" : "bg-cyan-100 text-cyan-800"
        }`}>🩸 {t.flow_2}</span>
        <ArrowRight className={`w-3 h-3 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
          isDark ? "bg-purple-500/15 text-purple-200" : "bg-purple-100 text-purple-800"
        }`}>📈 {t.flow_3}</span>
      </div>

      {/* Two cards side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        {/* ── Card 1: Nutrition Plan ── */}
        <div
          className={`rounded-xl border p-3 border-l-4 ${cardShell} ${
            isDark ? "border-l-emerald-400" : "border-l-emerald-500"
          }`}
          data-testid="mdr-nutrition-card"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Utensils className={`w-4 h-4 ${nutritionAccent}`} />
            <h4 className={`text-[13px] font-extrabold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {t.n_title}
            </h4>
          </div>
          <p className={`text-[10.5px] mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.n_sub}</p>

          {/* iter1108 — "When to look?" callout for instant purpose clarity */}
          <div
            className={`rounded-md px-2 py-1.5 mb-2 border ${
              isDark
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-100"
                : "bg-emerald-50 border-emerald-200 text-emerald-900"
            }`}
            data-testid="mdr-n-when"
          >
            <div className={`text-[9px] uppercase tracking-wider font-bold ${nutritionAccent}`}>
              🕒 {t.n_when_label}
            </div>
            <div className={`text-[10.5px] leading-snug ${isDark ? "text-emerald-100/90" : "text-emerald-900/90"}`}>
              {t.n_when_body}
            </div>
          </div>

          <div className={`grid grid-cols-2 gap-1.5 text-[11px] ${isDark ? "text-slate-200" : "text-slate-800"}`}>
            <div className={`rounded-lg px-2 py-1.5 ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
              <div className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.n_kcal}</div>
              <div className="text-sm font-bold tabular-nums" data-testid="mdr-n-kcal">{kcal ?? "—"}</div>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
              <div className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.n_protein}</div>
              <div className="text-sm font-bold tabular-nums" data-testid="mdr-n-protein">{proteinG != null ? `${proteinG} g` : "—"}</div>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
              <div className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.n_carbs}</div>
              <div className="text-sm font-bold tabular-nums" data-testid="mdr-n-carbs">{carbsG != null ? `${carbsG} g` : "—"}</div>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
              <div className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.n_fat}</div>
              <div className="text-sm font-bold tabular-nums" data-testid="mdr-n-fat">{fatG != null ? `${fatG} g` : "—"}</div>
            </div>
          </div>

          <div className={`mt-2 pt-2 border-t ${isDark ? "border-slate-700/50" : "border-slate-200"}`}>
            <div className={`text-[9.5px] uppercase tracking-wider font-bold mb-1 ${nutritionAccent}`}>{t.n_used_for}</div>
            <ul className={`text-[10.5px] leading-snug space-y-0.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {t.n_uses.map((u) => <li key={u}>✅ {u}</li>)}
            </ul>
          </div>
        </div>

        {/* ── Card 2: Daily Insulin Budget ── */}
        <div
          className={`rounded-xl border p-3 border-l-4 ${cardShell} ${
            isDark ? "border-l-cyan-400" : "border-l-cyan-500"
          }`}
          data-testid="mdr-dil-card"
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Droplet className={`w-4 h-4 ${dilAccent}`} />
            <h4 className={`text-[13px] font-extrabold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {t.d_title}
            </h4>
          </div>
          <p className={`text-[10.5px] mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.d_sub}</p>

          {/* iter1108 — "When to look?" callout for instant purpose clarity */}
          <div
            className={`rounded-md px-2 py-1.5 mb-2 border ${
              isDark
                ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-100"
                : "bg-cyan-50 border-cyan-200 text-cyan-900"
            }`}
            data-testid="mdr-d-when"
          >
            <div className={`text-[9px] uppercase tracking-wider font-bold ${dilAccent}`}>
              🕒 {t.d_when_label}
            </div>
            <div className={`text-[10.5px] leading-snug ${isDark ? "text-cyan-100/90" : "text-cyan-900/90"}`}>
              {t.d_when_body}
            </div>
          </div>

          {/* iter1100 — Big DIL limit OR "profile incomplete" CTA */}
          {gap ? (
            <div
              data-testid="mdr-d-gap"
              className={`rounded-lg p-2.5 mb-2 border ${
                isDark ? "bg-amber-500/10 border-amber-500/30 text-amber-100"
                       : "bg-amber-50 border-amber-300 text-amber-900"
              }`}
            >
              <div className="text-[11.5px] font-extrabold leading-snug mb-1">
                ⚠️ {t.d_gap_title}
              </div>
              <p className={`text-[10.5px] leading-snug mb-2 ${isDark ? "text-amber-100/85" : "text-amber-900/85"}`}>
                {t.d_gap_lead}
              </p>

              {/* iter1101 — Inline field inputs (no redirect). Save + auto-regen. */}
              <div className="grid grid-cols-3 gap-1.5 mb-2" data-testid="mdr-d-gap-inputs">
                {gapMissing.map((f) => (
                  <label key={f} className="flex flex-col gap-0.5">
                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? "text-amber-200/80" : "text-amber-800/80"}`}>
                      {t.d_gap_labels?.[f] || f}
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step={f === "age" ? "1" : "0.1"}
                      placeholder={t.d_gap_placeholders?.[f] || ""}
                      value={gapValues[f] ?? ""}
                      onChange={(e) => setGapValues((s) => ({ ...s, [f]: e.target.value }))}
                      disabled={gapSaving}
                      data-testid={`mdr-d-gap-input-${f}`}
                      className={`text-[13px] font-bold tabular-nums px-2 py-1 rounded border outline-none transition-colors ${
                        isDark
                          ? "bg-slate-900/70 border-amber-500/40 text-white focus:border-amber-400 placeholder:text-slate-500"
                          : "bg-white border-amber-300 text-slate-900 focus:border-amber-500 placeholder:text-slate-400"
                      }`}
                    />
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleGapSave}
                  disabled={!gapReady || gapSaving}
                  data-testid="mdr-d-gap-save"
                  className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full transition ${
                    !gapReady || gapSaving
                      ? (isDark ? "bg-slate-700 text-slate-400 cursor-not-allowed" : "bg-slate-200 text-slate-400 cursor-not-allowed")
                      : (isDark ? "bg-amber-500 hover:bg-amber-400 text-slate-900" : "bg-amber-600 hover:bg-amber-700 text-white")
                  }`}
                >
                  {gapSaving ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> {t.d_gap_saving}</>
                  ) : (
                    <><Save className="w-3 h-3" /> {t.d_gap_inline_save}</>
                  )}
                </button>
                <a
                  href="/profile?edit=1"
                  data-testid="mdr-d-gap-cta"
                  className={`text-[10.5px] font-semibold underline ${
                    isDark ? "text-amber-200/80 hover:text-amber-100" : "text-amber-800/80 hover:text-amber-900"
                  }`}
                >
                  {t.d_gap_cta}
                </a>
              </div>
            </div>
          ) : (
            <div className={`rounded-lg p-2 mb-2 text-center ${
              isDark ? "bg-cyan-500/10 border border-cyan-500/25" : "bg-cyan-50 border border-cyan-200"
            }`}>
              <div className={`text-[9px] uppercase tracking-wider font-bold ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>{t.d_limit_label}</div>
              <div className={`text-2xl font-black tabular-nums leading-tight ${isDark ? "text-cyan-200" : "text-cyan-800"}`} data-testid="mdr-d-limit">
                {ceilingG ?? "—"} <span className="text-sm font-bold opacity-70">{t.d_limit_unit}</span>
              </div>
              {goalLabel && (
                <div className={`text-[9px] mt-0.5 ${isDark ? "text-cyan-300/80" : "text-cyan-700/80"}`}>
                  · {goalLabel} ·
                </div>
              )}
            </div>
          )}

          {/* iter1100 — Body-fat missing warning (only когато DIL е валиден но липсват обиколки). */}
          {!gap && breakdown?.body_fat_missing && (
            <div
              data-testid="mdr-d-bf-warning"
              className={`rounded-lg p-2 mb-2 border ${
                isDark ? "bg-slate-800/60 border-slate-600/50 text-slate-200"
                       : "bg-slate-50 border-slate-300 text-slate-700"
              }`}
            >
              <div className="text-[11px] font-bold leading-snug mb-0.5">📏 {t.d_bf_missing_title}</div>
              <p className={`text-[10px] leading-snug mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {t.d_bf_missing_hint}
              </p>

              {/* iter1101 — Inline inputs за обиколки, без redirect. */}
              <div className={`grid gap-1.5 mb-2 ${bfRequired.length === 3 ? "grid-cols-3" : "grid-cols-2"}`} data-testid="mdr-d-bf-inputs">
                {bfRequired.map((f) => (
                  <label key={f} className="flex flex-col gap-0.5">
                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {t.d_bf_labels?.[f] || f}
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="0.1"
                      placeholder="cm"
                      value={bfValues[f] ?? ""}
                      onChange={(e) => setBfValues((s) => ({ ...s, [f]: e.target.value }))}
                      disabled={bfSaving}
                      data-testid={`mdr-d-bf-input-${f}`}
                      className={`text-[12px] font-bold tabular-nums px-2 py-1 rounded border outline-none transition-colors ${
                        isDark
                          ? "bg-slate-900/70 border-slate-600 text-white focus:border-cyan-400 placeholder:text-slate-500"
                          : "bg-white border-slate-300 text-slate-900 focus:border-cyan-500 placeholder:text-slate-400"
                      }`}
                    />
                  </label>
                ))}
              </div>

              <button
                type="button"
                onClick={handleBfSave}
                disabled={!bfReady || bfSaving}
                data-testid="mdr-d-bf-save"
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full transition ${
                  !bfReady || bfSaving
                    ? (isDark ? "bg-slate-700 text-slate-400 cursor-not-allowed" : "bg-slate-200 text-slate-400 cursor-not-allowed")
                    : (isDark ? "bg-cyan-500 hover:bg-cyan-400 text-slate-900" : "bg-cyan-600 hover:bg-cyan-700 text-white")
                }`}
              >
                {bfSaving ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> {t.d_bf_saving}</>
                ) : (
                  <><Save className="w-3 h-3" /> {t.d_bf_save}</>
                )}
              </button>
            </div>
          )}

          {/* iter938 — Hormonal-adjustment breakdown (females only when factor ≠ 1.0) */}
          {showHormonalHint && (
            <div
              className={`rounded-lg px-2 py-1.5 mb-2 border text-[10px] leading-snug ${
                isDark ? "bg-pink-500/10 border-pink-500/25 text-pink-100"
                       : "bg-pink-50 border-pink-200 text-pink-900"
              }`}
              data-testid="mdr-hormonal-hint"
            >
              <div className="font-semibold mb-0.5">
                {t.d_hormonal_intro}: <span className="font-mono">{baseCeiling} × {hormonalFactor} = {ceilingG}</span>
              </div>
              <div className={`grid grid-cols-2 gap-0.5 mt-1 text-[9.5px] ${isDark ? "text-pink-200/85" : "text-pink-800/85"}`}>
                <span>{t.d_hormonal_base}:</span>
                <span className="tabular-nums font-bold text-right">{baseCeiling} DIL</span>
                <span>{t.d_hormonal_factor}:</span>
                <span className="tabular-nums font-bold text-right">× {hormonalFactor}</span>
                {estrogenLabel && (
                  <>
                    <span>{t.d_hormonal_estrogen}:</span>
                    <span className="font-bold text-right">{estrogenLabel}</span>
                  </>
                )}
              </div>
              <p className={`italic text-[9px] mt-1 ${isDark ? "text-pink-300/70" : "text-pink-700/70"}`}>
                {t.d_hormonal_why}
              </p>
            </div>
          )}

          {/* Formula strip */}
          <div className={`rounded-lg px-2 py-1.5 mb-2 font-mono text-[10.5px] leading-snug ${
            isDark ? "bg-slate-950/50 text-slate-300" : "bg-slate-100 text-slate-700"
          }`} data-testid="mdr-d-formula">
            <div className={`text-[9px] uppercase tracking-wider font-bold font-sans mb-0.5 ${isDark ? "text-slate-500" : "text-slate-500"}`}>{t.d_formula_label}</div>
            {t.d_formula}
          </div>

          {/* Reference values */}
          {(refNetCarbsG != null || refProteinG != null) && (
            <div className="mb-2">
              <div className={`text-[9px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {t.d_reference_title}
              </div>
              <div className={`space-y-1 text-[10.5px] ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {refNetCarbsG != null && (
                  <div className="flex items-center justify-between" data-testid="mdr-d-ref-carbs">
                    <span>{t.d_ref_net_carbs} <span className="opacity-60 text-[9.5px]">({t.d_ref_net_carbs_hint})</span></span>
                    <span className="tabular-nums font-bold">{refNetCarbsG} g</span>
                  </div>
                )}
                {refProteinG != null && (
                  <div className="flex items-center justify-between" data-testid="mdr-d-ref-protein">
                    <span>{t.d_ref_protein}</span>
                    <span className="tabular-nums font-bold">{refProteinG} g</span>
                  </div>
                )}
                {proteinContribution != null && (
                  <div className={`flex items-center justify-between italic ${isDark ? "text-cyan-300/85" : "text-cyan-700/85"}`} data-testid="mdr-d-protein-contribution">
                    <span>↳ {t.d_protein_contribution}</span>
                    <span className="tabular-nums font-bold">{proteinContribution} DIL</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={`pt-2 border-t ${isDark ? "border-slate-700/50" : "border-slate-200"}`}>
            <div className={`text-[9.5px] uppercase tracking-wider font-bold mb-1 ${dilAccent}`}>{t.d_used_for}</div>
            <ul className={`text-[10.5px] leading-snug space-y-0.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {t.d_uses.map((u) => <li key={u}>✅ {u}</li>)}
            </ul>
          </div>
        </div>
      </div>

      {/* 14-day live progress */}
      {dilAvg != null && ceilingG != null && (
        <div
          className={`rounded-xl px-3 py-2 mb-3 border text-[11px] ${
            dilAvg <= ceilingG
              ? (isDark ? "bg-emerald-500/10 border-emerald-400/30" : "bg-emerald-50 border-emerald-200")
              : (isDark ? "bg-amber-500/10 border-amber-400/30"     : "bg-amber-50 border-amber-200")
          }`}
          data-testid="mdr-14d-progress"
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold">{t.live_title}: <span className="tabular-nums">{Math.round(dilAvg)} DIL</span></span>
            <span className="ml-auto tabular-nums font-semibold">
              {dilAvg <= ceilingG
                ? `✓ ${ceilingG - Math.round(dilAvg)} DIL ${t.live_under}`
                : `+${Math.round(dilAvg) - ceilingG} DIL ${t.live_over}`}
            </span>
          </div>
          {/* Progress bar */}
          <div className={`mt-1.5 h-1.5 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
            <div
              className={`h-full transition-all ${
                dilAvg <= ceilingG ? "bg-emerald-500" : "bg-amber-500"
              }`}
              style={{ width: `${Math.min(100, (dilAvg / ceilingG) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Basket comparison */}
      {carbsG != null && ceilingG != null && (
        <div className="mb-3" data-testid="mdr-baskets">
          <p className={`text-[10.5px] uppercase font-bold tracking-wider mb-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {t.b_title.replace("97g", `${carbsG}g`)}
          </p>
          <p className={`text-[11px] mb-2 leading-snug ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {t.b_intro_a}
            <b>{t.b_intro_bold}</b>
            {t.b_intro_b}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {baskets.map((b) => {
              const dil = basketDil(b);
              const within = dil != null && dil <= ceilingG;
              const paletteBg = {
                emerald: isDark ? "bg-emerald-500/10 border-emerald-400/30" : "bg-emerald-50 border-emerald-200",
                amber:   isDark ? "bg-amber-500/10 border-amber-400/30"     : "bg-amber-50 border-amber-200",
                rose:    isDark ? "bg-rose-500/10 border-rose-400/30"       : "bg-rose-50 border-rose-200",
              }[b.color];
              const paletteText = {
                emerald: isDark ? "text-emerald-200" : "text-emerald-800",
                amber:   isDark ? "text-amber-200"   : "text-amber-800",
                rose:    isDark ? "text-rose-200"    : "text-rose-800",
              }[b.color];
              const fiberG = Math.round(carbsG * b.fiber_ratio);
              return (
                <div
                  key={b.id}
                  className={`rounded-xl p-2.5 border ${paletteBg}`}
                  data-testid={`mdr-basket-${b.id}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-lg">{b.emoji}</span>
                    <span className={`text-[10.5px] font-bold ${paletteText}`}>≈ {dil} DIL <span className="opacity-80 font-normal">({t.b_net_carbs})</span></span>
                    <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                      within
                        ? (isDark ? "bg-emerald-500/25 text-emerald-100" : "bg-emerald-100 text-emerald-800")
                        : (isDark ? "bg-rose-500/25 text-rose-100"       : "bg-rose-100 text-rose-800")
                    }`}>
                      {within ? t.b_within_budget : `${t.b_over_by} ${dil - ceilingG}g`}
                    </span>
                  </div>
                  <p className={`text-[10.5px] font-semibold ${paletteText} leading-snug`}>
                    {carbsG}g {t.b_carbs_from} {b.label}
                  </p>
                  <p className={`text-[9.5px] mt-0.5 leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {b.hint} · фибри ≈ {fiberG}g
                  </p>
                </div>
              );
            })}
          </div>
          <p className={`text-[10px] mt-2 leading-snug italic ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {t.b_note}
          </p>
        </div>
      )}

      {/* Science drawer toggle */}
      <button
        type="button"
        onClick={() => setShowScience((v) => !v)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] font-semibold transition-colors ${
          isDark ? "bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]"
                 : "bg-slate-100 text-slate-700 hover:bg-slate-200"
        }`}
        data-testid="mdr-science-toggle"
      >
        <BookOpen className="w-3 h-3" />
        {showScience ? t.sci_toggle_hide : t.sci_toggle_show}
      </button>

      {showScience && (
        <div
          className={`mt-2 rounded-xl p-3 border text-[10.5px] leading-relaxed ${
            isDark ? "bg-slate-950/40 border-slate-700 text-slate-200"
                   : "bg-white border-slate-200 text-slate-700"
          }`}
          data-testid="mdr-science-body"
        >
          <p className="mb-2">
            <b>{t.sci_why_two}</b> {t.sci_why_two_body}
          </p>
          <p className="mb-2">
            <b>{t.sci_coeff}</b>
          </p>
          {breakdown?.rationale_bg && lang === "bg" && (
            <p className="mb-2">
              <b>{t.sci_your_protocol} ({goalLabel}):</b> {breakdown.rationale_bg}
            </p>
          )}
          {breakdown?.rationale_en && lang === "en" && (
            <p className="mb-2">
              <b>{t.sci_your_protocol} ({goalLabel}):</b> {breakdown.rationale_en}
            </p>
          )}
          <p className="mb-1 flex items-start gap-1">
            <Info className={`w-3 h-3 mt-0.5 shrink-0 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
            <span>
              <b>{t.sci_refs}:</b> Holt et al. 1997 <a href="https://pubmed.ncbi.nlm.nih.gov/9354739/" target="_blank" rel="noopener noreferrer" className="underline">PMID 9354739</a>,
              {" "}Ludwig 2018 BMJ <a href="https://pubmed.ncbi.nlm.nih.gov/30429100/" target="_blank" rel="noopener noreferrer" className="underline">PMID 30429100</a>
              {pmids.length > 0 && (
                <>
                  , {t.sci_your_protocol.toLowerCase()}:{" "}
                  {pmids.map((id, i) => (
                    <React.Fragment key={id}>
                      <a href={`https://pubmed.ncbi.nlm.nih.gov/${id}/`} target="_blank" rel="noopener noreferrer" className="underline">
                        PMID {id}
                      </a>
                      {i < pmids.length - 1 && ", "}
                    </React.Fragment>
                  ))}
                </>
              )}
              .{" "}
              <a href="https://pypi.org/project/nutramilo-nis/" target="_blank" rel="noopener noreferrer" className="underline">nutramilo-nis SDK</a>.
            </span>
          </p>
          <p className={`text-[9.5px] italic mt-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {t.sci_edu_only}
          </p>
        </div>
      )}
    </section>
  );
}
